// Generated by GitHub Copilot
import { useState } from "react";
import { FaDiscord, FaGithub, FaLinkedinIn, FaMediumM, FaRedditAlien } from "react-icons/fa";
import { buildApiUrl } from "../utils/api";

function Contact({ config, apiBaseUrl, profile, loading = false }) {
  const [status, setStatus] = useState("");
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({ name: "", email: "", message: "" });
  const collaborationConfig = config?.collaboration || {};
  const contactApiUrl = buildApiUrl("/contact", apiBaseUrl);
  const linkedinUrl = profile?.contact?.linkedin || "https://www.linkedin.com/in/ramprakashdhulipudi/";
  const githubUrl = profile?.contact?.github || "https://github.com/Ramdragneel01";
  const mediumUrl = profile?.contact?.medium || "https://medium.com/@RamPrakashD";
  const redditUrl = profile?.contact?.reddit || "https://www.reddit.com/user/Sad_Echo5408/";
  const discordUrl = profile?.contact?.discord || "https://discord.gg/mRUesvV3A";

  const socialLinks = [
    { label: "LinkedIn", href: linkedinUrl, Icon: FaLinkedinIn },
    { label: "GitHub", href: githubUrl, Icon: FaGithub },
    { label: "Medium", href: mediumUrl, Icon: FaMediumM },
    { label: "Reddit", href: redditUrl, Icon: FaRedditAlien },
    { label: "Discord", href: discordUrl, Icon: FaDiscord },
  ];

  if (loading) {
    return (
      <section className="py-16" id="contact">
        <h2 className="text-3xl font-bold">{config?.title}</h2>

        <div className="mt-6 grid gap-4 lg:grid-cols-[minmax(0,1fr)_17rem]">
          <div
            className="glass-card space-y-4 rounded-3xl p-6"
            role="status"
            aria-live="polite"
            aria-label="Loading contact form"
            aria-busy="true"
          >
            <div className="skeleton h-[48px] rounded-xl" />
            <div className="skeleton h-[48px] rounded-xl" />
            <div className="skeleton h-32 rounded-xl" />
            <div className="skeleton h-[44px] w-40 rounded-lg" />
          </div>

          <div
            className="glass-card rounded-3xl p-4"
            role="status"
            aria-live="polite"
            aria-label="Loading social links"
            aria-busy="true"
          >
            <div className="skeleton h-4 w-24 rounded-md" />
            <div className="mt-3 space-y-2">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={`social-skeleton-${index}`} className="skeleton h-10 rounded-xl" />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  /**
   * Validate user input before sending it to the backend contact endpoint.
   *
   * @returns {boolean} True when form fields are valid.
   */
  function validateForm() {
    const nextErrors = {
      name: "",
      email: "",
      message: "",
    };

    if (!form.name.trim() || form.name.trim().length < 2) {
      nextErrors.name = "Please enter at least 2 characters for your name.";
    }

    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      nextErrors.email = "Please provide a valid email address.";
    }

    if (!form.message.trim() || form.message.trim().length < 10) {
      nextErrors.message = "Please share a message with at least 10 characters.";
    }

    setErrors(nextErrors);
    return !nextErrors.name && !nextErrors.email && !nextErrors.message;
  }

  /**
   * Submit the contact form through the backend proxy endpoint.
   */
  async function handleSubmit(event) {
    event.preventDefault();

    if (!validateForm()) {
      setStatus("Please fix the highlighted fields before submitting.");
      return;
    }

    setStatus(config?.statuses?.sending || "Sending...");

    try {
      const response = await fetch(contactApiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          message: form.message,
        }),
      });

      if (!response.ok) {
        throw new Error(`Contact request failed: ${response.status}`);
      }

      setStatus(config?.statuses?.success || "Message sent successfully.");
      setForm({ name: "", email: "", message: "" });
      setErrors({ name: "", email: "", message: "" });
    } catch (error) {
      setStatus(config?.statuses?.failure || "Could not send message right now.");
      console.error(error);
    }
  }

  return (
    <section className="py-16" id="contact">
      <h2 className="text-3xl font-bold">{config?.title}</h2>

      {collaborationConfig?.title ? (
        <article className="glass-card mt-6 rounded-3xl p-6">
          <h3 className="text-xl font-semibold">{collaborationConfig.title}</h3>
          {collaborationConfig.summary ? (
            <p className="mt-2 text-sm text-[var(--text-secondary)]">{collaborationConfig.summary}</p>
          ) : null}

          {Array.isArray(collaborationConfig.topics) && collaborationConfig.topics.length ? (
            <ul className="mt-4 list-disc space-y-1.5 pl-5 text-sm text-[var(--text-secondary)]">
              {collaborationConfig.topics.map((topic) => (
                <li key={topic}>{topic}</li>
              ))}
            </ul>
          ) : null}

          {Array.isArray(collaborationConfig.availability) && collaborationConfig.availability.length ? (
            <div className="mt-4 flex flex-wrap gap-2">
              {collaborationConfig.availability.map((item) => (
                <span key={item} className="glass-chip rounded-full px-3 py-1 text-xs text-[var(--text-primary)]">
                  {item}
                </span>
              ))}
            </div>
          ) : null}

          {collaborationConfig.responseExpectation ? (
            <p className="mt-4 text-sm text-[var(--text-secondary)]">
              <span className="font-semibold text-[var(--text-primary)]">Response expectation:</span>{" "}
              {collaborationConfig.responseExpectation}
            </p>
          ) : null}
        </article>
      ) : null}

      <div className="mt-6 grid gap-4 lg:grid-cols-[minmax(0,1fr)_17rem] lg:items-start">
        <form className="glass-card w-full space-y-4 rounded-3xl p-6" onSubmit={handleSubmit}>
          <input
            className="glass-input w-full px-4 py-3"
            type="text"
            value={form.name}
            onChange={(event) => {
              setForm((prev) => ({ ...prev, name: event.target.value }));
              setErrors((prev) => ({ ...prev, name: "" }));
            }}
            placeholder={config?.placeholders?.name || "Your name"}
            required
            aria-invalid={Boolean(errors.name)}
          />
          {errors.name ? <p className="text-sm text-red-500">{errors.name}</p> : null}
          <input
            className="glass-input w-full px-4 py-3"
            type="email"
            value={form.email}
            onChange={(event) => {
              setForm((prev) => ({ ...prev, email: event.target.value }));
              setErrors((prev) => ({ ...prev, email: "" }));
            }}
            placeholder={config?.placeholders?.email || "Your email"}
            required
            aria-invalid={Boolean(errors.email)}
          />
          {errors.email ? <p className="text-sm text-red-500">{errors.email}</p> : null}
          <textarea
            className="glass-input min-h-32 w-full px-4 py-3"
            value={form.message}
            onChange={(event) => {
              setForm((prev) => ({ ...prev, message: event.target.value }));
              setErrors((prev) => ({ ...prev, message: "" }));
            }}
            placeholder={config?.placeholders?.message || "Your message"}
            required
            aria-invalid={Boolean(errors.message)}
          />
          {errors.message ? <p className="text-sm text-red-500">{errors.message}</p> : null}
          <button
            className="glass-button-primary rounded-lg px-5 py-3 font-semibold transition"
            type="submit"
          >
            {config?.submitButtonLabel || "Send Message"}
          </button>
          <p className="text-sm text-[var(--text-secondary)]" aria-live="polite">
            {status}
          </p>
        </form>

        <aside className="glass-card rounded-3xl p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[rgba(var(--accent-rgb),0.95)]">
            Connect
          </p>
          <div className="mt-3 space-y-2">
            {socialLinks.map((socialLink) => (
              <a
                key={socialLink.label}
                href={socialLink.href}
                target="_blank"
                rel="noreferrer"
                className="glass-button-secondary flex w-full items-center gap-3 rounded-full px-3 py-2.5 text-sm font-semibold"
                aria-label={socialLink.label}
              >
                <socialLink.Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span>{socialLink.label}</span>
              </a>
            ))}
          </div>
        </aside>
      </div>
    </section>
  );
}

export default Contact;
